/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { customLanguageDetector, normalizeLanguageCode, createChromeTranslateGuard } from '../utils/language-detector';
import { resources } from './i18n-resources';

// Configure LanguageDetector with custom detector
const languageDetector = new LanguageDetector();
languageDetector.addDetector(customLanguageDetector);

export const i18nInitPromise = i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources,
    supportedLngs: ['en', 'pt-BR', 'es', 'pt'],
    nonExplicitSupportedLngs: true,
    fallbackLng: {
      'pt': ['pt-BR'],
      'pt-PT': ['pt-BR'],
      'es-AR': ['es'],
      'es-MX': ['es'],
      'en-US': ['en'],
      'en-GB': ['en'],
      'default': ['pt-BR'],
    },
    load: 'currentOnly',
    defaultNS: 'common',
    ns: ['common', 'location', 'features/properties', '@features/properties', 'features/visits', 'features/members', 'features/blog', 'features/popups', 'features/filters', 'features/pricing', 'features/clients', '@features/clients', 'features/cookie-consent', 'features/agency-settings', 'features/agenda', 'features/deals', 'features/attendances', 'features/leases', 'features/sales', 'features/vistories', 'features/partnerships', 'features/launches', 'features/product-tour', 'features/subscription', 'features/spreadsheets', 'features/assistant', '@pages/landing', '@pages/early-access', '@pages/not-found', '@pages/error', '@pages/agencies', '@pages/properties', '@pages/manage-agencies', '@pages/manage-properties', '@pages/register-agency', '@pages/property-pages', '@pages/layout', '@pages/policy', '@pages/onboarding', '@pages/welcome', '@pages/user-settings', '@pages/public-property', '@pages/data-rights', '@pages/visits'],
    interpolation: {
      escapeValue: false,
    },
    initImmediate: false,
    detection: {
      // `?lang=` is the only way a first-time visitor reaches en/es, and it is
      // treated as an explicit choice (cached like any other). After it comes
      // the stored preference, and only then the pt-BR default. The browser's
      // own language is not in this chain on purpose -- see
      // `customLanguageDetector`.
      order: ['querystring', 'localStorage', 'customLanguageDetector'],
      caches: ['localStorage'],
      lookupQuerystring: 'lang',
      lookupLocalStorage: 'i18nextLng',
    },
  });

// Intercept i18next language changes to respect consent
// Only cache language preference if user has accepted consent
i18n.on('languageChanged', (lng: string) => {
  const normalized = normalizeLanguageCode(lng);
  if (normalized !== lng) {
    i18n.changeLanguage(normalized);
    return;
  }

  try {
    localStorage.setItem('i18nextLng', lng);
  } catch {
    // Ignore errors (e.g., in private browsing mode)
  }
});

// Guard against Chrome Translate mutating <html lang> from "pt-BR" to "pt"
const chromeTranslateGuard = createChromeTranslateGuard((normalizedLang) => {
  if (i18n.language !== normalizedLang) {
    i18n.changeLanguage(normalizedLang);
  }
});
chromeTranslateGuard.observe();

export default i18n;
